import { config } from '../shared/config';
import { currentAccessToken } from './supabase';

/**
 * The dashboard's HTTP client.
 *
 * Deliberately not the `api` export from `../shared/api`: that one falls back
 * to mock data when the backend is unreachable, which is right for a phone in a
 * field-test build and wrong for an office portal, where an empty jobs table
 * must mean "no jobs" and never "the network died". Every failure surfaces.
 *
 * Types come from `../shared/api` (a symlink to the repo's `lib/api.ts`) so
 * both surfaces speak one vocabulary.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly details?: unknown,
    readonly payload?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** True for the flat 403 the permission middleware emits. */
export function isPermissionDenied(err: unknown): err is ApiError {
  return err instanceof ApiError && err.status === 403 && err.message === 'PERMISSION_DENIED';
}

/**
 * The backend closes every authenticated route until the account accepts the
 * Early Access Agreement, so any call can come back with this. The redirect is
 * wired once here rather than at every call site.
 */
export const BETA_AGREEMENT_REQUIRED = 'beta_agreement_required';

export function isBetaAgreementRequired(err: unknown): err is ApiError {
  return err instanceof ApiError && err.status === 403 && err.message === BETA_AGREEMENT_REQUIRED;
}

type BetaListener = (currentVersion: string | null) => void;
const betaListeners = new Set<BetaListener>();

/** Subscribe to that 403; returns the unsubscribe. */
export function onBetaAgreementRequired(fn: BetaListener): () => void {
  betaListeners.add(fn);
  return () => {
    betaListeners.delete(fn);
  };
}

export interface RequestOptions {
  /** Extra headers, e.g. the x-lg-seat-token capability on public parcel doors. */
  headers?: Record<string, string>;
}

async function request<T>(method: string, path: string, body?: unknown, opts?: RequestOptions): Promise<T> {
  const token = await currentAccessToken();
  const headers: Record<string, string> = { Accept: 'application/json', ...opts?.headers };
  if (token) headers.Authorization = `Bearer ${token}`;
  let payload: BodyInit | undefined;
  if (body instanceof FormData) {
    payload = body;
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }

  let res: Response;
  try {
    res = await fetch(`${config.apiUrl}${path}`, { method, headers, body: payload });
  } catch {
    throw new ApiError(0, `Could not reach the LienGuardian API at ${config.apiUrl}`);
  }
  const text = await res.text();
  const json = text ? safeParse(text) : null;
  if (!res.ok) {
    const err = new ApiError(
      res.status,
      (json?.error as string) ?? `Request failed (${res.status})`,
      json?.details,
      json ?? undefined,
    );
    if (isBetaAgreementRequired(err)) {
      const version = typeof json?.currentVersion === 'string' ? json.currentVersion : null;
      for (const fn of [...betaListeners]) fn(version);
    }
    throw err;
  }
  return json as T;
}

function safeParse(text: string): Record<string, unknown> | null {
  try {
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, undefined, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('POST', path, body, opts),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PUT', path, body, opts),
  del: <T>(path: string) => request<T>('DELETE', path),
};

export const fileUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  return url.startsWith('http') ? url : `${config.apiUrl}${url}`;
};
